/**
 * ARAÇ EŞİTLEME — seçilen araçların fiyatı (olaylarıyla, tek istekte), kuru
 * ve portföy grafiği önbelleğinin haberi. Dönemsel Getiri ile aynı kurallar
 * (`DonemselAnalizScreen`): döviz "araçları" (`kur:USD`) fiyat istemez,
 * yalnızca kuru eşitlenir; merkezin kur kaynağı yalnızca X/TRY yayımlar.
 *
 * Sanal hesap (back-test, 14.10.2026) ve ölçülerdeki kıyas serisi bunu
 * kullanır. Merkeze giden istek yalnızca araç kimliği ve tarih aralığı taşır
 * (`transport.ts` beyaz listesi); kural, oran, tutar GİTMEZ.
 */

import { isKnownCurrency, type CurrencyCode, type IsoDate } from "@parafon/contracts";
import { KUR_KIYAS_ONEKI, type LocalRepository } from "@parafon/contracts/local-api";
import type { FxSyncResult, SyncReport } from "@parafon/contracts/device-api";
import type { CentralContextValue } from "../central/CentralContext";
import { esitlemeninDegisimi } from "../central/EsitlemeSaglayici";
import { M } from "../dil";
import { fiyatlarDegisti } from "./seriOnbellegi";

function mesaj(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Merkezin kur kaynağı yalnızca X/TRY yayımlıyor. */
export function kurCiftleri(paralar: readonly (string | null)[]): (readonly [CurrencyCode, CurrencyCode])[] {
  const k = new Set<string>();
  for (const p of paralar) if (p !== null && p !== "TRY" && isKnownCurrency(p)) k.add(p);
  return [...k].sort().map((c) => [c, "TRY"] as const);
}

/** Oturumda bir kez: aynı araçlar + aralık + paralar yeniden eşitlenmez. */
const esitlenen = new Set<string>();

export interface EsitlemeSonucu {
  /** Eşitleme denendi mi (merkez yoksa ya da daha önce yapıldıysa false). */
  readonly yapildi: boolean;
  readonly uyarilar: readonly string[];
}

export async function araclariEsitle(
  repo: LocalRepository,
  merkez: Pick<CentralContextValue, "central" | "prices" | "sync">,
  istek: {
    readonly instrumentIds: readonly string[];
    readonly from: IsoDate;
    readonly to: IsoDate;
    /** Kur gereken para birimleri (araçların ve hesabın/raporun). */
    readonly paralar: readonly (string | null)[];
  },
): Promise<EsitlemeSonucu> {
  const { central, prices, sync } = merkez;
  if (central === null || prices === null || sync === null) return { yapildi: false, uyarilar: [] };
  const araclar = istek.instrumentIds.filter((id) => !id.startsWith(KUR_KIYAS_ONEKI)).sort();
  const dovizler = istek.instrumentIds.filter((id) => id.startsWith(KUR_KIYAS_ONEKI)).map((id) => id.slice(KUR_KIYAS_ONEKI.length));
  const anahtar = `${araclar.join(",")}|${[...istek.paralar, ...dovizler].join(",")}|${istek.from}|${istek.to}`;
  if (esitlenen.has(anahtar)) return { yapildi: false, uyarilar: [] };
  const uyarilar: string[] = [];
  let fiyat: SyncReport | null = null;
  let kur: FxSyncResult | null = null;
  let kirildi = false;
  if (araclar.length > 0) {
    try {
      fiyat = await sync.syncPrices({
        cache: prices,
        central,
        instrumentIds: araclar,
        from: istek.from,
        to: istek.to,
        ensureTracked: true,
      });
      uyarilar.push(...fiyat.errors);
    } catch (err) {
      kirildi = true;
      uyarilar.push(M.izleme.esitleme.fiyatHatasi(mesaj(err)));
    }
  }
  // Aracın para birimi künyeden (eşitleme künyeyi de yazar).
  const kunyeParalari: (string | null)[] = [];
  if (araclar.length > 0) {
    try {
      for (const k of await prices.instrumentStates(araclar)) kunyeParalari.push(k.currency);
    } catch {
      /* künye okunamazsa yalnızca bilinen paralar */
    }
  }
  const ciftler = kurCiftleri([...istek.paralar, ...dovizler, ...kunyeParalari]);
  if (ciftler.length > 0) {
    try {
      kur = await sync.syncFx({ cache: prices, central, pairs: ciftler, from: istek.from, to: istek.to });
      uyarilar.push(...kur.errors);
    } catch (err) {
      kirildi = true;
      uyarilar.push(M.izleme.esitleme.kurHatasi(mesaj(err)));
    }
  }
  const degisim = esitlemeninDegisimi(fiyat, kur, kirildi);
  fiyatlarDegisti(degisim);
  if (degisim !== null) await repo.seriOnbellegi?.sil(degisim).catch(() => undefined);
  if (!kirildi && uyarilar.length === 0) esitlenen.add(anahtar);
  return { yapildi: true, uyarilar };
}

/** Testler için. */
export function aracEsitlemeBelleginiSifirla(): void {
  esitlenen.clear();
}
